import * as THREE from 'three';
import { Sky } from 'three/examples/jsm/objects/Sky.js';

/**
 * Physical sky (three's Sky: Preetham scattering) with a fixed afternoon sun, the matching sun light,
 * and an environment map pre-filtered from the same sky for image-based ambient light and reflections.
 * The sky dome follows the camera.
 */
export class SkyRig {
  readonly sky = new Sky();
  readonly sun: THREE.DirectionalLight;
  /** Unit vector toward the sun (three space: y up). */
  readonly sunDir: THREE.Vector3;
  /** Hazy horizon colour, used for fog so distant tiles fade into the sky. */
  readonly horizon = new THREE.Color(0xc9d6e2);

  constructor(renderer: THREE.WebGLRenderer, scene: THREE.Scene, elevationDeg = 38, azimuthDeg = 215) {
    const phi = THREE.MathUtils.degToRad(90 - elevationDeg);
    const theta = THREE.MathUtils.degToRad(azimuthDeg);
    this.sunDir = new THREE.Vector3().setFromSphericalCoords(1, phi, theta);

    const u = this.sky.material.uniforms;
    u.turbidity.value = 3.5;
    u.rayleigh.value = 1.2;
    u.mieCoefficient.value = 0.004;
    u.mieDirectionalG.value = 0.82;
    u.sunPosition.value.copy(this.sunDir);
    // The sun disc reaches ~60 000 before the rest of the sky is added, past half float's 65 504:
    // stored in the scene target it becomes infinite, and bloom and tone mapping spread that into
    // black frames. 30 000 is still far beyond white at any exposure.
    const sm = this.sky.material;
    sm.fragmentShader = sm.fragmentShader.replace('gl_FragColor = vec4( texColor, 1.0 );', 'gl_FragColor = vec4( min( texColor, vec3( 30000.0 ) ), 1.0 );');
    if (!sm.fragmentShader.includes('vec3( 30000.0 )')) console.warn('sky: sun clamp not applied');
    sm.needsUpdate = true;
    this.sky.scale.setScalar(6000);
    this.sky.frustumCulled = false;
    scene.add(this.sky);

    this.sun = new THREE.DirectionalLight(0xffe6c4, 6.5);
    this.sun.position.copy(this.sunDir).multiplyScalar(1000);
    scene.add(this.sun);
    scene.add(this.sun.target);

    // Environment: render the sky alone into a PMREM (once; the sun is fixed).
    const envScene = new THREE.Scene();
    const envSky = new Sky();
    envSky.scale.setScalar(1000);
    const eu = envSky.material.uniforms;
    for (const k of Object.keys(u)) if (eu[k] && typeof u[k].value !== 'object') eu[k].value = u[k].value;
    eu.sunPosition.value.copy(this.sunDir);
    eu.showSunDisc.value = 0;
    envScene.add(envSky);
    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(envScene, 0, 0.1, 2000).texture;
    scene.environmentIntensity = 0.3;
    pmrem.dispose();

    scene.background = null;
    scene.fog = new THREE.Fog(this.horizon, 1800, 6000);
  }

  /** Keep the dome centred on the camera and the sun light's shadow frustum near it. */
  update(camera: THREE.Camera): void {
    this.sky.position.copy(camera.position);
    this.sun.target.position.copy(camera.position);
    this.sun.position.copy(camera.position).addScaledVector(this.sunDir, 1000);
  }
}
